import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Layout,
  theme,
  Avatar,
  Dropdown,
  Space,
  Typography,
  Badge,
} from 'antd';
import {
  BellOutlined,
  LogoutOutlined,
  UserSwitchOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import Navbar from '../components/navigation/Navbar';
import { UserProfile, LogoutButton } from '../components/auth';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { logout, user, isAuthenticated } = useAuth0();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  // User dropdown menu
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile Settings',
    },
    {
      key: 'switch',
      icon: <UserSwitchOutlined />,
      label: 'Switch to Coach View',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: () => logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      }),
    },
  ];



  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
        theme="light"
        width={250}
      >
        {/* Logo */}
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? 0 : '0 24px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          {!collapsed ? (
            <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
              Fit Manager
            </Text>
          ) : (
            <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
              FM
            </Text>
          )}
        </div>

        {/* Navigation Menu */}
        <Navbar
          mode="inline"
          theme="light"
          collapsed={collapsed}
          style={{ height: 'calc(100% - 64px)', borderRight: 0 }}
        />
      </Sider>

      {/* Main Layout */}
      <Layout style={{ marginLeft: collapsed ? 80 : 250, transition: 'margin-left 0.2s' }}>
        {/* Header */}
        <Header 
          style={{ 
            padding: '0 24px', 
            background: colorBgContainer,
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Collapse Toggle */}
            <button
              onClick={() => setCollapsed(!collapsed)}
              style={{
                border: 'none',
                background: 'none',
                fontSize: 16,
                cursor: 'pointer',
                padding: 8,
                borderRadius: 4,
                transition: 'all 0.2s',
              }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          </div>

          {/* Header Actions */}
          <Space size="middle">
            {/* Notifications */}
            <Badge count={5} size="small">
              <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} />
            </Badge>

            {/* User Menu */}
            {isAuthenticated ? (
              <Dropdown
                menu={{ items: userMenuItems }}
                placement="bottomRight"
                arrow
              >
                <Space style={{ cursor: 'pointer' }}>
                  <Avatar 
                    size="small" 
                    src={user?.picture}
                    icon={<UserOutlined />} 
                  />
                  <Text>{user?.name || 'User'}</Text>
                </Space>
              </Dropdown>
            ) : (
              <LogoutButton />
            )}
          </Space>
        </Header>

        {/* Main Content */}
        <Content
          style={{
            margin: 0,
            padding: 24,
            minHeight: 280,
            background: '#f5f5f5',
          }}
        >
          <div
            style={{
              padding: 24,
              minHeight: 360,
              background: colorBgContainer,
              borderRadius: 8,
            }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout; 